import { useCallback, useContext, useMemo, useState } from 'react';
import { withTypes } from 'react-final-form';
import { FormApi } from 'final-form';
import set from 'lodash.set';
import get from 'lodash.get';
import CurrentProfileContext from '../providers/CurrentProfile/CurrentProfile.context';
import { User } from 'models/User';
import { getContactNumber, getInitialPhone } from 'phoneNumberUtils';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import mixpanelService from 'services/mixpanelService';
import { PhoneNumberSetupInitiatedEventProps } from 'models/MixpanelEvents';

export type UpdatePasswordValues = {
  oldPassword: string;
  newPassword: string;
};

export type ProfileFormValues = Partial<User> & UpdatePasswordValues;

export const { Form: ProfileForm } = withTypes<ProfileFormValues>();

export default function useProfileForm() {
  const [isLoading, setIsLoading] = useState(false);

  const { profileFormValues, patchCurrentProfile } = useContext(
    CurrentProfileContext,
  );

  const initialData = useMemo(
    () =>
      ({
        phone: getInitialPhone(),
        ...profileFormValues,
      } as ProfileFormValues),
    [profileFormValues],
  );

  const getMixpanelProps = useGetMixpanelProps();

  const patchUserRequest = useCallback(
    async (
      values: Partial<ProfileFormValues>,
      onSuccess?: (verificationOverrides: Partial<ProfileFormValues>) => void,
    ) => {
      // Each profile page is now its own form, so only the fields registered on
      // the submitted form are present. Apply the contact/company handling based
      // on which fields are actually there, otherwise saving one form (e.g.
      // Preferences) would clobber fields it doesn't even render.
      const hasEmail = 'email' in values;
      const hasPhone = 'phone' in values;

      const {
        oldPassword,
        newPassword,
        profileImage,
        email,
        phone,
        ...patchableValues
      } = values;

      (['linkedinAccount', 'companyName', 'companyABN'] as const).forEach(
        (key) => {
          if (key in patchableValues) {
            patchableValues[key] = patchableValues[key] || null;
          }
        },
      );

      const phoneChanged =
        hasPhone &&
        getContactNumber(phone) !== getContactNumber(initialData?.phone);
      const emailChanged = hasEmail && email !== initialData?.email;

      // Changing a verified contact invalidates its verification, so clear the
      // flag alongside the new value.
      const verificationOverrides: Partial<ProfileFormValues> = {
        ...(emailChanged && { hasVerifiedEmail: false }),
        ...(phoneChanged && { hasVerifiedContactNumber: false }),
      };

      try {
        setIsLoading(true);

        if (phoneChanged && getContactNumber(phone)) {
          mixpanelService.phoneNumberSetupInitiated(
            getMixpanelProps({
              phoneNumber: getContactNumber(phone),
            }) as PhoneNumberSetupInitiatedEventProps,
          );
        }

        await patchCurrentProfile?.(
          {
            ...patchableValues,
            ...(hasPhone && { phone }),
            ...(emailChanged && { email }),
            ...verificationOverrides,
          },
          {
            onSuccess: () => {
              setIsLoading(false);
              onSuccess?.(verificationOverrides);
            },
          },
        );
      } catch {
        return;
      } finally {
        setIsLoading(false);
      }
    },
    [
      initialData?.email,
      initialData?.phone,
      getMixpanelProps,
      patchCurrentProfile,
    ],
  );

  const onSubmit = useCallback(
    async (values: ProfileFormValues, form: FormApi<ProfileFormValues>) => {
      const modifiedKeys = Object.keys(form.getState().modified || {});

      const modifiedValues = modifiedKeys.reduce(
        (acc, key) => set(acc, key, get(values, key)),
        {} as Partial<ProfileFormValues>,
      );

      // `keepDirtyOnReinitialize` keeps in-progress edits while images are
      // uploaded/deleted, but that also stops the form going pristine after a
      // save. Restart it on success so the FloatingActions bar hides, seeding
      // the new baseline with any verification flags the save just cleared.
      await patchUserRequest(modifiedValues, (verificationOverrides) =>
        form.restart({ ...values, ...verificationOverrides }),
      );
    },
    [patchUserRequest],
  );

  return { isLoading, initialData, onSubmit };
}
